import React, { ChangeEvent, FocusEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { PayloadAction } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DescriptionOutlined as DescriptionOutlinedIcon } from '@mui/icons-material';
import { Typography } from '@mui/material';
import Button from '@ui-elements/Button';
import BasicModal from '@ui-parts/BasicModal';
import InputWithLabel from '@ui-parts/InputWithLabel';

import { useAppDispatch } from '~/App/hooks';
import { RootState } from '~/App/store';
import { getAddressesByZipCode, postReceiptRequest } from '~/apiTs';
import { FONT_SIZES, FONT_WEIGHTS, PALETTE } from '~/assets/styles/base';
import { AddressesByZipCodeResponse } from '~/common/types/api';
import {
  prefValidationRule,
  receiptRequestNameValidationRule,
  requiredAddressValidationRule,
  zipCodeValidationRule,
} from '~/constants/validationRules';

const FlexWrapper = styled.form`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  & div[data-testid='uiParts-InputValidationErrorBox-alert-receipt-requests-zipcode'] {
    left: 100%;
    &:before,
    :after {
      left: 25%;
    }
  }
  & div[data-testid='uiParts-InputValidationErrorBox-alert-receipt-requests-pref'] {
    left: 100%;
    &:before,
    :after {
      left: 28%;
    }
  }
`;

const ModalFooterInner = styled.div`
  padding: 8px 0 20px;
`;

const StyledButton = styled(Button)`
  &&&.MuiButton-root.MuiButton-contained {
    font-size: ${FONT_SIZES.body3};
    color: ${PALETTE.white};
    background-color: #4aa7f7;
    box-shadow: 1px 2px 2px #00000014;
    width: 148px;
    height: 40px;
  }
`;

const TopModal = styled.div`
  display: flex;
  gap: 9px;
  font-weight: ${FONT_WEIGHTS.size3};
  color: ${PALETTE.text.main};
  padding: 40px 30px 23px;
`;

const Title = styled.div`
  font-size: ${FONT_SIZES.title1};
  line-height: 23px;
  padding-bottom: 6px;
`;

const Description = styled.div`
  font-size: ${FONT_SIZES.title2};
  line-height: 24px;
`;

const InputNameGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 11px;
  border-bottom: 1px solid ${PALETTE.border.modalItemBottom};
  padding-bottom: 16px;
`;

type Props = {
  open: boolean;
  handleClose: () => void;
  surveyId: number;
  setAlert: (isOpen: boolean) => void;
};

const ReceiptRequestsModal: React.FC<Props> = ({
  open,
  handleClose,
  surveyId,
  setAlert,
}): JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.user);

  const { control, handleSubmit, setValue, getValues, clearErrors, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      name: user.name,
      zipcode: user.zip,
      pref: user.pref,
      address: user.address,
    },
  });

  const onCloseModal = () => {
    clearErrors();
    reset();
    handleClose();
  };

  const getAddress = async () => {
    if (getValues('zipcode').match(zipCodeValidationRule.pattern.value) != null) {
      const zipCode = getValues('zipcode');
      const { payload: addressInfo } = (await dispatch(
        getAddressesByZipCode({ zipCode })
      )) as unknown as PayloadAction<
        AddressesByZipCodeResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;
      setValue('pref', addressInfo.result[0].prefChar);
      setValue('address', addressInfo.result[0].cityChar + addressInfo.result[0].townChar);
    }
  };

  type ReceiptRequestsForm = {
    name: string;
    zipcode: string;
    pref: string;
    address: string;
  };
  const onSubmit = async (param: ReceiptRequestsForm) => {
    const params = {
      ...param,
      surveyId,
    };

    const { payload: response } = (await dispatch(
      postReceiptRequest(params)
    )) as unknown as PayloadAction<
      AddressesByZipCodeResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;

    if (response.status === 0) {
      setAlert(true);
      onCloseModal();
    }
  };

  return (
    <BasicModal
      open={open}
      titleProps={{
        title: t('receiptRequests.title'),
        desc: t('receiptRequests.description'),
        customModalTitle: (
          <TopModal>
            <DescriptionOutlinedIcon fontSize="large" sx={{ color: PALETTE.text.sub }} />
            <div>
              <Title data-testid="receiptRequests-title">{t('receiptRequests.title')}</Title>
              <Description data-testid="receiptRequests-desc1">
                {t('receiptRequests.description1')}
              </Description>
              <Description data-testid="receiptRequests-desc2">
                {t('receiptRequests.description2')}
              </Description>
            </div>
          </TopModal>
        ),
      }}
      handleClose={() => onCloseModal()}
      buttons={[
        <ModalFooterInner key="ModalFooterInner">
          <StyledButton variant="contained" handleClick={handleSubmit(onSubmit)} type="submit">
            {t('receiptRequests.title')}
          </StyledButton>
        </ModalFooterInner>,
      ]}
      dataTestId="ReceiptRequestsModal"
      width="600px"
    >
      <FlexWrapper data-testid="receiptRequests-form">
        <Controller
          name="name"
          control={control}
          rules={receiptRequestNameValidationRule}
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => {
            const inputProps = {
              name,
              value,
              handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e);
              },
              handleBlur: (e: FocusEvent<HTMLInputElement>): void => {
                setValue('name', e.target.value.trim());
              },
              handleOnKeyDown: (e: React.KeyboardEvent<HTMLInputElement>): void => {
                if (e.key === 'Enter') {
                  setValue('name', (e.target as HTMLInputElement).value.trim());
                }
              },
              error: Boolean(error),
              errorMessage: error?.message,
              errorWidth: 'max-content',
              dataTestId: 'receipt-requests-name',
              width: '400px',
              fontWeight: FONT_WEIGHTS.size3,
              inputProps: { maxLength: 100 },
            };
            return (
              <InputNameGroup>
                <InputWithLabel {...inputProps} ref={ref} />
                <Typography data-testid="receipt-requests-nameLabel" sx={{ paddingTop: '5px' }}>
                  {t('receiptRequests.name')}
                </Typography>
              </InputNameGroup>
            );
          }}
        />
        <Controller
          name="zipcode"
          control={control}
          rules={zipCodeValidationRule}
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => {
            const inputProps = {
              label: t('receiptRequests.zipcode'),
              placeholder: '0000000',
              name,
              value,
              handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e);
                getAddress();
              },
              handleBlur: (e: FocusEvent<HTMLInputElement>): void => {
                setValue(name, e.target.value.trim());
              },
              handleOnKeyDown: (e: React.KeyboardEvent<HTMLInputElement>): void => {
                if (e.key === 'Enter') {
                  setValue(name, (e.target as HTMLInputElement).value.trim());
                }
              },
              error: Boolean(error),
              errorMessage: error?.message,
              errorWidth: 'max-content',
              dataTestId: 'receipt-requests-zipcode',
              width: '132px',
              fontWeight: FONT_WEIGHTS.size3,
              inputProps: { maxLength: 7 },
            };
            return <InputWithLabel {...inputProps} ref={ref} />;
          }}
        />
        <Controller
          name="pref"
          control={control}
          rules={prefValidationRule}
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => {
            const inputProps = {
              label: t('receiptRequests.pref'),
              name,
              value,
              handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e);
              },
              handleBlur: (e: FocusEvent<HTMLInputElement>): void => {
                setValue(name, e.target.value.trim());
              },
              handleOnKeyDown: (e: React.KeyboardEvent<HTMLInputElement>): void => {
                if (e.key === 'Enter') {
                  setValue(name, (e.target as HTMLInputElement).value.trim());
                }
              },
              error: Boolean(error),
              errorMessage: error?.message,
              errorWidth: 'max-content',
              dataTestId: 'receipt-requests-pref',
              width: '132px',
              fontWeight: FONT_WEIGHTS.size3,
              inputProps: { maxLength: 32 },
            };
            return <InputWithLabel {...inputProps} ref={ref} />;
          }}
        />
        <Controller
          name="address"
          control={control}
          rules={requiredAddressValidationRule}
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => {
            const inputProps = {
              label: t('receiptRequests.address'),
              name,
              value,
              handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e);
              },
              handleBlur: (e: FocusEvent<HTMLInputElement>): void => {
                setValue(name, e.target.value.trim());
              },
              handleOnKeyDown: (e: React.KeyboardEvent<HTMLInputElement>): void => {
                if (e.key === 'Enter') {
                  setValue(name, (e.target as HTMLInputElement).value.trim());
                }
              },
              error: Boolean(error),
              errorMessage: error?.message,
              errorWidth: 'max-content',
              dataTestId: 'receipt-requests-address',
              fontWeight: FONT_WEIGHTS.size3,
              inputProps: { maxLength: 128 },
            };
            return <InputWithLabel {...inputProps} ref={ref} />;
          }}
        />
      </FlexWrapper>
    </BasicModal>
  );
};

export default ReceiptRequestsModal;
